import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, NgZone } from '@angular/core';
import { BASE_URI, config } from 'app/app.config';
import { BehaviorSubject, map, Observable, Subject } from 'rxjs';

declare var annyang: any;

export enum aiVoiceState {
  Idle = 'idle',
  Listening = 'listening',
  Speaking = 'speaking',
  Thinking = 'thinking',
  Stopped = 'stopped'
}


export type AiVoiceState = 'idle' | 'listening' | 'speaking' | 'thinking' | 'stopped';

export interface QItem {
  question_id: number;
  question: string;
  answer: string;
}

export interface caption {
  id: string;
  text: string;
  isFinal: boolean;
  timestramp: number;
}

@Injectable({
  providedIn: 'root'
})
export class AskQuestionService {

  private agentPrivateUrl = "";
  private agentGenericUrl = "";
  baseUrl = BASE_URI.URL;

  historyList: any[] = [];
  voice: SpeechSynthesisVoice[] = [];
  state: aiVoiceState = aiVoiceState.Idle;
  private captionSubject$ = new Subject<caption>();
  caption$ = this.captionSubject$.asObservable();

  public state$ = new BehaviorSubject<AiVoiceState>('idle');
  private readonly synth = typeof window !== 'undefined' ? window.speechSynthesis : null;


  private readonly dataUrl = 'assets/js/agenticai.json';
  private readonly BaseURL = BASE_URI.URL;
  private currentUtterances: SpeechSynthesisUtterance[] = [];
  private utteranceSeq = 0;

  private recognition: any;
  private recognitionActive = false;
  private recognitionStarting = false;
  private recognitionInterrupted = false;
  private micSessionId = 0;

  private activeRecognitionSession = 0;

  private onResultCallback: any = null;
  private onEndCallback: any = null;

  onResultMatchCallback: ((userSaid: string, commandText: string, phrases: string[]) => void) | null = null;

  constructor(
    private readonly ngZone: NgZone,
    private readonly http: HttpClient
  ) {
    if (annyang) {
      // Debug mode (optional)
      annyang.debug(true);
    }

    this.loadVoices();

    window.speechSynthesis.onvoiceschanged = () => {
      const voices = speechSynthesis.getVoices();
    };
  }

  get currentState(): AiVoiceState {
    return this.state$.value;
  }

  public loadVoices(): void {
    if (!this.synth) return;

    const load = () => {
      const voices = this.synth!.getVoices();
      if (voices.length > 0) {
        this.voice = voices;
      }
    };

    // Try immediately
    load();

    // Listen only once for async loading
    this.synth.onvoiceschanged = () => {
      load();
    };
  }

  speak(
    text: string,
    voiceName?: string,
    rate = 1,
    pitch = 1,
    lang?: string,
    callback?: () => void
  ) {
    if (!text) return;

    if (!this.synth) {
      this.emitCaption(text, true);
      return;
    }

    this.stopSpeaking();
    this.state = aiVoiceState.Speaking;
    this.synth.cancel();

    this.currentUtterances = [];
    this.utteranceSeq++;

    const utterance = new SpeechSynthesisUtterance(text);
    if (lang) utterance.lang = lang;
    utterance.rate = rate;
    utterance.pitch = pitch;


    // Voice selection
    if (voiceName) {
      const voices = this.synth.getVoices();

      // Try to find a Microsoft female voice in en-US
      const microsoftFemaleVoices = voices.filter(v =>
        v.lang.toLowerCase().startsWith('en-us') &&
        /microsoft/i.test(v.name) &&
        (/female/i.test(v.name) || /zira/i.test(v.name)) // common MS female voice names
      );

      if (microsoftFemaleVoices.length > 0) {
        utterance.voice = microsoftFemaleVoices[0];
      } else {

        utterance.voice = voices.find(v => v.name === voiceName || v.lang === voiceName) ?? voices[0];
      }
    }

    // Streaming captions with boundary support
    if ('onboundary' in SpeechSynthesisUtterance.prototype) {
      const captionBuffer: any[] = [];
      utterance.onboundary = (event: SpeechSynthesisEvent) => {
        const charIndex = typeof event.charIndex === 'number' ? event.charIndex : null;
        if (charIndex !== null) {
          const textSoFar = text.slice(0, charIndex + 1);
          const chunks: any = textSoFar.split(/(?<=[.!?])\s+|\n/);
          const lastChunk = (chunks.length > 0 ? chunks[chunks.length - 1] : textSoFar).trim();

          if (lastChunk && lastChunk !== captionBuffer[captionBuffer.length - 1]) {
            this.ngZone.run(() => this.emitCaption(lastChunk, false));
            captionBuffer.push(lastChunk);
          }
        }
      };
    } else {
      const chunks = this.splitToChunks(text);
      this.speakChunksSequentially(chunks, utterance.voice ?? undefined, lang);
      return;
    }

    // Events
    utterance.onstart = () => {
      this.state = aiVoiceState.Speaking;
      const firstChunk = this.splitToChunks(text)[0] ?? text;
      this.emitCaption(firstChunk.trim(), false);
    };

    utterance.onend = () => {
      this.state = aiVoiceState.Idle;
      this.emitCaption(text.trim(), true);
      callback?.();
    };

    utterance.onerror = () => {
      this.state = aiVoiceState.Idle;
      this.emitCaption(text.trim(), true);
    };

    this.ngZone.runOutsideAngular(() => {
      this.currentUtterances.push(utterance);
      this.synth!.speak(utterance);
    });
  }

  splitToChunks(text: string): string[] {
    const rawText = text.split(/(?<=[.!?])\s+|\n/).map(s => s.trim()).filter(Boolean);
    const maxLength = 120;

    const out: string[] = [];

    for (const x of rawText) {
      if (x.length <= maxLength) {
        out.push(x);
      } else {
        const words = x.split(/\s+/);
        let cur = '';

        for (const w of words) {
          if ((cur + ' ' + w).trim().length <= maxLength) {
            cur = (cur + ' ' + w).trim();
          } else {
            if (cur) out.push(cur);
            cur = w;
          }
        }
        if (cur) out.push(cur);

      }
    }

    return out;
  }

  private speakChunksSequentially(chunks: string[], voice: SpeechSynthesisVoice | undefined, lang?: string) {
    if (!this.synth) return;
    const speakNext = (index: number) => {
      if (index >= chunks.length) {
        const combined = chunks.join(' ').trim();
        this.emitCaption(combined, true);
        return;
      }
      const u = new SpeechSynthesisUtterance(chunks[index]);
      if (lang) u.lang = lang;
      if (voice) u.voice = voice;
      u.rate = 1;
      u.pitch = 1;

      u.onstart = () => {
        this.emitCaption(chunks[index], index === chunks.length - 1);
      };
      u.onend = () => speakNext(index + 1);
      u.onerror = () => speakNext(index + 1);

      this.ngZone.runOutsideAngular(() => this.synth!.speak(u));
    };

    // cancel previous then start
    this.synth.cancel();
    speakNext(0);
  }

  emitCaption(text: string, isFinal: boolean) {
    const caption: caption = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      text: text,
      timestramp: Date.now(),
      isFinal: !!isFinal
    };

    this.ngZone.run(() => this.captionSubject$.next(caption));
  }



  stopSpeaking(): void {
    if (!this.synth) return;

    if (this.synth.speaking || this.synth.pending) {
      this.synth.cancel();

      // Reset state safely
      this.state = aiVoiceState.Stopped;
    }
  }

  startListening(
    callback: (query: string, isFinal?: boolean, confidence?: number) => void
  ) {

    if (this.recognitionStarting || this.recognitionActive) return;

    const SpeechRecognition =
      (window as any).SpeechRecognition ||
      (window as any).webkitSpeechRecognition;

    if (!SpeechRecognition) {
      console.error('SpeechRecognition not supported');
      return;
    }

    this.recognitionStarting = true;

    try {

      // cleanup previous instance
      this.cleanupRecognition();

      const recognition = new SpeechRecognition();

      this.recognition = recognition;

      const sessionId = ++this.micSessionId;

      this.recognitionActive = true;
      this.recognitionInterrupted = false;

      recognition.lang = 'en-US';
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.maxAlternatives = 1;

      recognition.onstart = () => {
        console.log('Speech recognition started');
      };

      recognition.onresult = (event: any) => {

        if (!this.recognitionActive) return;

        if (sessionId !== this.micSessionId) return;

        let interim = '';
        let final = '';

        for (let i = event.resultIndex; i < event.results.length; i++) {

          const transcript = event.results[i][0].transcript;

          if (event.results[i].isFinal) {
            final += transcript;
          } else {
            interim += transcript;
          }
        }

        if (interim.trim()) {
          callback(interim.trim(), false, 1);
        }

        if (final.trim()) {
          callback(final.trim(), true, 1);
        }
      };

      recognition.onerror = (event: any) => {
        console.error('Speech error:', event.error);
        this.recognitionActive = false;
      };

      recognition.onend = () => {
        console.log('Speech ended');
        this.recognitionActive = false;
      };

      setTimeout(() => {
        recognition.start();
      }, 250);

    } catch (err) {

      console.error('Recognition failed:', err);

      this.recognitionActive = false;
    }

    setTimeout(() => {
      this.recognitionStarting = false;
    }, 500);
  }

  stopListening(manual = true) {

    const annyang = (window as any).annyang;

    if (!annyang) return;

    try {

      this.recognitionActive = false;

      if (manual) {
        this.recognitionInterrupted = true;
      }

      this.micSessionId++;

      this.cleanupRecognition();

      try {
        annyang.abort();
      } catch { }

      // this.setState('stopped');

    } catch (err) {
      console.error('Error stopping annyang:', err);
    }
  }

  cleanupRecognition() {

    try {

      if (this.recognition) {

        this.recognition.onstart = null;
        this.recognition.onresult = null;
        this.recognition.onerror = null;
        this.recognition.onend = null;

        this.recognition.stop();
        this.recognition.abort();
      }

    } catch { }

    this.recognition = null;
    this.recognitionActive = false;
  }

  getAnswers(query: string): Observable<string | null> {
    return this.http.get<QItem[]>(this.dataUrl).pipe(
      map((items: QItem[]) => {
        const normalizedQuery = query.toLowerCase();
        // 1. Exact match
        const exactMatch: any = items.find(item =>
          item.question.toLowerCase() === normalizedQuery
        );

        return exactMatch.answer;

      })
    );
  }

  createHistory(payload: any) {
    return this.http.post(`${this.BaseURL}/createHistory`, payload);
  }

  getAIHistoryData(payload: any) {

    const Params = this.preHttpParams(payload)
    let user_id = window.localStorage.getItem('user_id');
    return this.http.get(`${this.BaseURL}/getChatBotHistory?user_id=${user_id}`, { params: Params });
  }

  deleteHistory(id:any){
    let user_id = window.localStorage.getItem('user_id');
    return this.http.delete(`${this.baseUrl}/deleteHistory/${id}/${user_id}`);
  }

  createComments(payload:any){
     return this.http.post(`${this.baseUrl}/createAIFeedback`,payload);
   }


  preHttpParams(payload: any) {
    let Params = new HttpParams();
    for (const key in payload) {
      if (key && (payload[key] || payload[key] === 0)) {
        Params = Params.append(key, payload[key]);
      }
    }
    return Params;
  }
}
